import { NoteViewer } from "@/components/note/viewer/note-viewer";
import { css, keyframes } from "@/domains/emotion";
import useDebouncedCallback from "@/domains/react/useDebouncedCallback";
import { AppStoreNavigationStore } from "@/store/navigation/AppStoreNavigationStore";
import { INoteObservable } from "@/store/note/types";
import { useAppStore } from "@/store/utils/hooks";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useRef } from "react";

interface QuickSearchModalNotePreviewProps {
  noteObservable: INoteObservable;
}

export const QuickSearchModalNotePreview = observer(function QuickSearchModalNotePreview({
  noteObservable,
}: QuickSearchModalNotePreviewProps) {
  const { store, pageStore } = useAppStore();
  const state = pageStore.quickSearchModal;

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const animationFrameIdRef = useRef<number | null>(null);

  const resetPreviewScroll = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    wrapper.scrollTop = 0;
  }, []);

  const resetPreviewScrollAfterRender = useDebouncedCallback(
    (noteId: string) => {
      if (noteId !== noteObservable.id) return;

      animationFrameIdRef.current = window.requestAnimationFrame(() => {
        animationFrameIdRef.current = null;
        resetPreviewScroll();
      });
    },
    150,
    { leading: false, trailing: true }
  );

  useEffect(() => {
    return () => {
      resetPreviewScrollAfterRender.cancel();

      if (animationFrameIdRef.current !== null) {
        window.cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
    };
  }, [resetPreviewScrollAfterRender]);

  const handleNoteRendered = useCallback(
    (noteId: string) => {
      resetPreviewScroll();
      resetPreviewScrollAfterRender(noteId);
    },
    [resetPreviewScroll, resetPreviewScrollAfterRender]
  );

  useEffect(() => {
    resetPreviewScroll();
  }, [noteObservable.id, resetPreviewScroll]);

  const noteViewerStyles = css({
    animation: `${growAndFadeAnimation} .2s ease-out`,
    backgroundColor: "transparent",
    minHeight: "100%",
    overflowAnchor: "none",

    "> [data-theme]": {
      justifyContent: "flex-start",
    },
  });

  const goToMention = useCallback(
    (args: Parameters<AppStoreNavigationStore["goToMention"]>[0]) => {
      store.navigation.goToMention(args);
      state.close();
    },
    [store.navigation, state]
  );

  const goToNote = useCallback(() => {
    store.navigation.goToNote({ noteId: noteObservable.id });
    state.close();
  }, [store.navigation, state, noteObservable.id]);

  return (
    <Wrapper ref={wrapperRef}>
      <NoteViewer
        className={noteViewerStyles}
        noteObservable={noteObservable}
        showMetadata={false}
        goToMention={goToMention}
        observeChanges={false}
        onReadOnlyNoteClicked={goToNote}
        onNoteRendered={handleNoteRendered}
      />
    </Wrapper>
  );
});

const Wrapper = styled.div`
  box-sizing: border-box;
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow: hidden;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing["7"]};
  scrollbar-width: none;
  width: clamp(0px, 640px, 100% - 8px);
  /* Ensure preview doesn't exceed viewport */
  max-height: calc(100% - ${({ theme }) => theme.spacing["8"]});

  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  background-color: ${({ theme }) => theme.colors.surface.paneHeader};

  @media (max-width: 900px) {
    display: none;
  }

  ::-webkit-scrollbar {
    display: none;
  }
`;

const growAndFadeAnimation = keyframes({
  from: {
    opacity: 0,
    transform: "scale(0.97)",
  },
  to: {
    opacity: 1,
    transform: "scale(1)",
  },
});
